import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const USER_DATA_KEY = 'userData';
const AuthContext = createContext(null);

function uuid4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('idle'); // idle | loading | succeeded | failed
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const login = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const params = new URLSearchParams(window.location.search);
      const demo = params.get('demo');
      const otp = params.get('otp');

      // Resume an existing session (page refresh, direct nav to a round page, ?save=true)
      // unless the URL is explicitly asking for a fresh demo/OTP login.
      const stored = sessionStorage.getItem(USER_DATA_KEY);
      if (stored && !demo && !otp) {
        setUser(JSON.parse(stored));
        setStatus('succeeded');
        return;
      }

      // Fresh login (not a resumed session) — clear any leftover completion
      // state from a previous player so a new login always starts at Rules.
      sessionStorage.removeItem('diwaliHuntCompleted');
      sessionStorage.removeItem('diwaliHuntStatId');

      let userData;
      if (demo) {
        const randomString = 'demo' + Math.floor(Math.random() * 10000);
        userData = {
          userId: randomString,
          name: randomString,
          email: `${randomString}@gmail.com`,
          sessionId: 'demo' + Math.floor(Math.random() * 100000),
          organizationId: 'demo' + Math.floor(Math.random() * 100000),
          role: 'demobypass',
          companyLogoUrl: process.env.REACT_APP_EM_LOGO,
          backButtonRedirect: process.env.REACT_APP_BASE_URL,
        };
      } else {
        const role = params.get('role');
        if (!otp) throw new Error('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
        }

        const headers = {
          'client-secret': process.env.REACT_APP_CLIENT_SECRET,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          throw new Error('Login failed');
        }
        const d = response.data.data;

        userData = {
          userId: d.userId || d.id,
          name: `${d.firstName || ''} ${d.lastName || ''}`.trim(),
          email: d.email || '',
          employeeId: d.employeeId || '',
          sessionId: d.sessionId,
          organizationId: d.organizationId,
          role: role === 'GUEST_USER' ? 'GUEST_USER' : 'ORG_USER',
          companyLogoUrl: process.env.REACT_APP_EM_LOGO,
          backButtonRedirect: process.env.REACT_APP_BASE_URL,
        };

        try {
          const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${d.organizationId}`);
          const companyLogo = orgRes.data?.organization?.companyLogo;
          if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY' && companyLogo) {
            const s3Source = process.env.REACT_APP_S3_SOURCE || '';
            const base = s3Source.endsWith('/') ? s3Source : `${s3Source}/`;
            userData.companyLogoUrl = `${base}${companyLogo}`;
          }
        } catch {
          // fall back to the default EM logo already set above
        }
      }

      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      setUser(userData);
      setStatus('succeeded');
    } catch (err) {
      const apiMsg = err.response?.data?.message;
      setError(apiMsg || err.message || 'Login failed');
      setStatus('failed');
    }
  }, []);

  useEffect(() => {
    login();
  }, [login]);

  const clearUser = useCallback(() => {
    setUser(null);
    sessionStorage.removeItem(USER_DATA_KEY);
    sessionStorage.removeItem('diwaliHuntCompleted');
    sessionStorage.removeItem('diwaliHuntStatId');
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, error, clearUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
